import { useState, useEffect, useCallback, useRef } from 'react';
import { getSocket } from '../lib/socket';
import * as simulationApi from '../lib/simulation.api';
import type {
  SimulationState,
  SimulationUpdate,
  AgentActivityData,
  SimulationEventData,
} from '../types/simulation.types';

interface UseSimulationOptions {
  planId: string;
  autoConnect?: boolean;
}

export const useSimulation = ({ planId, autoConnect = false }: UseSimulationOptions) => {
  const [state, setState] = useState<SimulationState | null>(null);
  const [events, setEvents] = useState<SimulationEventData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const socketRef = useRef(getSocket());
  const isConnectedRef = useRef(false);

  // Fetch initial state
  const fetchState = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const currentState = await simulationApi.getSimulationState(planId);
      setState(currentState);
    } catch (err: any) {
      // Don't show error for 401 or 404 (common when no simulation exists yet)
      if (err.response?.status !== 401 && err.response?.status !== 404) {
        setError(err.response?.data?.message || err.message || 'Failed to fetch simulation state');
      }
    } finally {
      setIsLoading(false);
    }
  }, [planId]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      const simulationEvents = await simulationApi.getSimulationEvents(planId);
      setEvents(simulationEvents);
    } catch (err: any) {
      // Silently fail for events - they're not critical
      if (err.response?.status !== 401 && err.response?.status !== 404) {
        console.error('Failed to fetch simulation events:', err);
      }
    }
  }, [planId]);

  // Start simulation
  const start = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const newState = await simulationApi.startSimulation(planId);
      setState(newState);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to start simulation';
      setError(errorMessage);
      console.error('Simulation start error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [planId]);

  // Stop simulation
  const stop = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await simulationApi.stopSimulation(planId);
      setState(null);
    } catch (err: any) {
      setError(err.message || 'Failed to stop simulation');
    } finally {
      setIsLoading(false);
    }
  }, [planId]);

  // Pause simulation
  const pause = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const newState = await simulationApi.pauseSimulation(planId);
      setState(newState);
    } catch (err: any) {
      setError(err.message || 'Failed to pause simulation');
    } finally {
      setIsLoading(false);
    }
  }, [planId]);

  // Resume simulation
  const resume = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const newState = await simulationApi.resumeSimulation(planId);
      setState(newState);
    } catch (err: any) {
      setError(err.message || 'Failed to resume simulation');
    } finally {
      setIsLoading(false);
    }
  }, [planId]);

  // Set speed
  const setSpeed = useCallback(
    async (speed: 1 | 2 | 4 | 8) => {
      try {
        setIsLoading(true);
        setError(null);
        const newState = await simulationApi.setSimulationSpeed(planId, speed);
        setState(newState);
      } catch (err: any) {
        setError(err.message || 'Failed to set simulation speed');
      } finally {
        setIsLoading(false);
      }
    },
    [planId]
  );

  // Handle WebSocket updates
  useEffect(() => {
    const socket = socketRef.current;

    if (!socket.connected || !planId) {
      return;
    }

    isConnectedRef.current = true;

    const handleSimulationUpdate = (update: SimulationUpdate) => {
      if (update.planId !== planId) return;

      console.log('Simulation update:', update.type, update);

      switch (update.type) {
        case 'STATE_CHANGED':
          setState(update.data);
          break;

        case 'AGENT_MOVED':
          setState((prev) => {
            if (!prev) return prev;
            const { agentId, position } = update.data;
            return {
              ...prev,
              agentPositions: prev.agentPositions.map((pos) =>
                pos.agentId === agentId ? position : pos
              ),
            };
          });
          break;

        case 'ACTIVITY_STARTED':
        case 'ACTIVITY_COMPLETED':
          // Could track activities in separate state if needed
          console.log('Activity update:', update.data);
          break;

        case 'PROGRESS_UPDATE':
          // Could show progress in UI
          const { agentId, progress } = update.data;
          console.log(`Agent ${agentId} progress: ${progress}%`);
          break;

        case 'EVENT_OCCURRED':
          setEvents((prev) => [...prev, update.data]);
          break;

        case 'SIMULATION_STOPPED':
          setState(null);
          break;

        default:
          console.log('Unknown simulation update type:', update.type);
      }
    };

    // Subscribe to simulation updates
    socket.on('simulation:update', handleSimulationUpdate);

    return () => {
      socket.off('simulation:update', handleSimulationUpdate);
      isConnectedRef.current = false;
    };
  }, [planId]);

  // Auto-fetch state on mount if autoConnect is true
  useEffect(() => {
    if (autoConnect) {
      fetchState();
      fetchEvents();
    }
  }, [autoConnect, fetchState, fetchEvents]);

  return {
    state,
    events,
    isLoading,
    error,
    actions: {
      start,
      stop,
      pause,
      resume,
      setSpeed,
      fetchState,
      fetchEvents,
    },
  };
};
